import { defineStore } from "pinia";
import type { FeatureMessageDTO, TermInfoDTO } from "@wkt/api/user/userDTO";

interface AppDialogsState {
  logoutOpen: boolean;
  applicationUpdatedOpen: boolean;
  confirmDialogOpen: boolean;
  confirmDialogTitle: string;
  confirmDialogMessage: string;
  confirmDialogAcceptLabel: string;
  confirmDialogCancelLabel: string;
  confirmDialogInstruction: string;
  confirmDialogToken: string;
  confirmDialogError: string;
  confirmDialogOnConfirm: (() => void) | null;
  confirmDialogOnCancel: (() => void) | null;
  directMessageOpen: boolean;
  directMessageOriginalMessageId: number | null;
  directMessageRecipientId: number | null;
  directMessageRecipientFirstname: string;
  directMessageRecipientLastname: string;
  directMessageRecipientDepartmentName: string;
  directMessageRecipientProfilePicture: string;
  directMessageTitle: string;
  directMessageBody: string;
  incidenceOpen: boolean;
  incidenceDraft: string;
  incidenceSubmitting: boolean;
  pendingTermsOpen: boolean;
  pendingTermsSubmitting: boolean;
  pendingTerms: TermInfoDTO[];
  pendingTermIndex: number;
  featureMessagesOpen: boolean;
  featureMessagesLoading: boolean;
  featureMessages: FeatureMessageDTO[];
  featureMessageIndex: number;
  acceptedTermsOpen: boolean;
  acceptedTermsLoading: boolean;
  acceptedTerms: TermInfoDTO[];
  acceptedTermIndex: number;
}

export const useAppDialogsStore = defineStore("appDialogs", {
  state: (): AppDialogsState => ({
    logoutOpen: false,
    applicationUpdatedOpen: false,
    confirmDialogOpen: false,
    confirmDialogTitle: "",
    confirmDialogMessage: "",
    confirmDialogAcceptLabel: "",
    confirmDialogCancelLabel: "",
    confirmDialogInstruction: "",
    confirmDialogToken: "",
    confirmDialogError: "",
    confirmDialogOnConfirm: null,
    confirmDialogOnCancel: null,
    directMessageOpen: false,
    directMessageOriginalMessageId: null,
    directMessageRecipientId: null,
    directMessageRecipientFirstname: "",
    directMessageRecipientLastname: "",
    directMessageRecipientDepartmentName: "",
    directMessageRecipientProfilePicture: "",
    directMessageTitle: "",
    directMessageBody: "",
    incidenceOpen: false,
    incidenceDraft: "",
    incidenceSubmitting: false,
    pendingTermsOpen: false,
    pendingTermsSubmitting: false,
    pendingTerms: [],
    pendingTermIndex: 0,
    featureMessagesOpen: false,
    featureMessagesLoading: false,
    featureMessages: [],
    featureMessageIndex: 0,
    acceptedTermsOpen: false,
    acceptedTermsLoading: false,
    acceptedTerms: [],
    acceptedTermIndex: 0,
  }),
  actions: {
    openLogout() {
      this.logoutOpen = true;
    },
    closeLogout() {
      this.logoutOpen = false;
    },
    openApplicationUpdated() {
      this.applicationUpdatedOpen = true;
    },
    closeApplicationUpdated() {
      this.applicationUpdatedOpen = false;
    },
    openConfirmDialog(payload: {
      title?: string | null;
      message?: string | null;
      acceptLabel?: string | null;
      cancelLabel?: string | null;
      confirmationInstruction?: string | null;
      confirmationToken?: string | null;
      confirmationError?: string | null;
      onConfirm?: (() => void) | null;
      onCancel?: (() => void) | null;
    }) {
      this.confirmDialogTitle = payload.title ?? "";
      this.confirmDialogMessage = payload.message ?? "";
      this.confirmDialogAcceptLabel = payload.acceptLabel ?? "";
      this.confirmDialogCancelLabel = payload.cancelLabel ?? "";
      this.confirmDialogInstruction = payload.confirmationInstruction ?? "";
      this.confirmDialogToken = payload.confirmationToken ?? "";
      this.confirmDialogError = payload.confirmationError ?? "";
      this.confirmDialogOnConfirm = payload.onConfirm ?? null;
      this.confirmDialogOnCancel = payload.onCancel ?? null;
      this.confirmDialogOpen = true;
    },
    closeConfirmDialog() {
      this.confirmDialogOpen = false;
      this.confirmDialogTitle = "";
      this.confirmDialogMessage = "";
      this.confirmDialogAcceptLabel = "";
      this.confirmDialogCancelLabel = "";
      this.confirmDialogInstruction = "";
      this.confirmDialogToken = "";
      this.confirmDialogError = "";
      this.confirmDialogOnConfirm = null;
      this.confirmDialogOnCancel = null;
    },
    openDirectMessageDialog(payload: {
      originalMessageId?: number | null;
      recipientId?: number | null;
      firstname?: string | null;
      lastname?: string | null;
      departmentName?: string | null;
      profilePicture?: string | null;
    }) {
      this.directMessageOriginalMessageId = payload.originalMessageId ?? null;
      this.directMessageRecipientId = payload.recipientId ?? null;
      this.directMessageRecipientFirstname = payload.firstname ?? "";
      this.directMessageRecipientLastname = payload.lastname ?? "";
      this.directMessageRecipientDepartmentName = payload.departmentName ?? "";
      this.directMessageRecipientProfilePicture = payload.profilePicture ?? "";
      this.directMessageTitle = "";
      this.directMessageBody = "";
      this.directMessageOpen = true;
    },
    closeDirectMessageDialog() {
      this.directMessageOpen = false;
      this.directMessageOriginalMessageId = null;
      this.directMessageRecipientId = null;
      this.directMessageRecipientFirstname = "";
      this.directMessageRecipientLastname = "";
      this.directMessageRecipientDepartmentName = "";
      this.directMessageRecipientProfilePicture = "";
      this.directMessageTitle = "";
      this.directMessageBody = "";
    },
    setDirectMessageTitle(value: string) {
      this.directMessageTitle = value;
    },
    setDirectMessageBody(value: string) {
      this.directMessageBody = value;
    },
    openIncidence() {
      this.incidenceOpen = true;
    },
    closeIncidence() {
      this.incidenceOpen = false;
      this.incidenceDraft = "";
      this.incidenceSubmitting = false;
    },
    setIncidenceDraft(value: string) {
      this.incidenceDraft = value;
    },
    setIncidenceSubmitting(value: boolean) {
      this.incidenceSubmitting = value;
    },
    openPendingTerms(terms: TermInfoDTO[]) {
      this.pendingTerms = terms;
      this.pendingTermIndex = 0;
      this.pendingTermsOpen = true;
      this.pendingTermsSubmitting = false;
    },
    closePendingTerms() {
      this.pendingTermsOpen = false;
      this.pendingTermsSubmitting = false;
      this.pendingTermIndex = 0;
      this.pendingTerms = [];
    },
    setPendingTermsSubmitting(value: boolean) {
      this.pendingTermsSubmitting = value;
    },
    nextPendingTerm() {
      if (this.pendingTermIndex < this.pendingTerms.length - 1) {
        this.pendingTermIndex += 1;
      }
    },
    openFeatureMessages(messages: FeatureMessageDTO[]) {
      this.featureMessages = messages;
      this.featureMessageIndex = 0;
      this.featureMessagesOpen = true;
      this.featureMessagesLoading = false;
    },
    closeFeatureMessages() {
      this.featureMessagesOpen = false;
      this.featureMessagesLoading = false;
      this.featureMessageIndex = 0;
      this.featureMessages = [];
    },
    setFeatureMessagesLoading(value: boolean) {
      this.featureMessagesLoading = value;
    },
    nextFeatureMessage() {
      if (this.featureMessageIndex < this.featureMessages.length - 1) {
        this.featureMessageIndex += 1;
      }
    },
    prevFeatureMessage() {
      if (this.featureMessageIndex > 0) {
        this.featureMessageIndex -= 1;
      }
    },
    openAcceptedTerms(terms: TermInfoDTO[]) {
      this.acceptedTerms = terms;
      this.acceptedTermIndex = 0;
      this.acceptedTermsOpen = true;
      this.acceptedTermsLoading = false;
    },
    closeAcceptedTerms() {
      this.acceptedTermsOpen = false;
      this.acceptedTermsLoading = false;
      this.acceptedTermIndex = 0;
      this.acceptedTerms = [];
    },
    setAcceptedTermsLoading(value: boolean) {
      this.acceptedTermsLoading = value;
    },
    nextAcceptedTerm() {
      if (this.acceptedTermIndex < this.acceptedTerms.length - 1) {
        this.acceptedTermIndex += 1;
      }
    },
    prevAcceptedTerm() {
      if (this.acceptedTermIndex > 0) {
        this.acceptedTermIndex -= 1;
      }
    },
  },
});
